import { RootState } from '@/store';
import { ActionContext, ActionTree } from 'vuex';
import { Mutations, MutationTypes } from './mutations';
import { State } from './state';
import ApiService from '@/services/ApiService';

import { Actions as CommonActions, ActionTypes as CommonActionTypes } from '../Common/actions';

import { Textbaustein, Vorlage, DeleteTextbausteinData, VorlageItem } from '@/models/VorlageModels';
import { PagedRequest, PagedResult } from '@/models/CommonModels';

export enum ActionTypes {
  GetVorlagen = 'GETVORLAGEN',
  GetVorlagenLight = 'GETVORLAGENLIGHT',
  GetVorlageById = 'GETVORLAGEBYID',
  GetVorlagenPage = 'GETVORLAGENPAGE',
  CreateVorlage = 'CREATEVORLAGE',
  UpdateVorlage = 'UPDATEVORLAGE',
  DeleteVorlage = 'DELETEVORLAGE',

  GetTextbausteine = 'GETTEXTBAUSTEINE',
  CreateTextbaustein = 'CREATETEXTBAUSTEIN',
  UpdateTextbaustein = 'UPDATETEXTBAUSTEIN',
  DeleteTextbaustein = 'DELETETEXTBAUSTEIN',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload?: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetVorlagen](context: ActionArguments): Promise<void>;
  [ActionTypes.GetVorlagenLight](context: ActionArguments): Promise<void>;
  [ActionTypes.GetVorlageById](context: ActionArguments, data: number): Promise<void>;
  [ActionTypes.GetVorlagenPage](context: ActionArguments, pagedRequest: PagedRequest): Promise<void>;
  [ActionTypes.CreateVorlage](context: ActionArguments, data: Vorlage): Promise<void>;
  [ActionTypes.UpdateVorlage](context: ActionArguments, data: Vorlage): Promise<void>;
  [ActionTypes.DeleteVorlage](context: ActionArguments, vorlageid: number): Promise<void>;

  [ActionTypes.GetTextbausteine](context: ActionArguments): Promise<void>;
  [ActionTypes.CreateTextbaustein](context: ActionArguments, data: Textbaustein): Promise<void>;
  [ActionTypes.UpdateTextbaustein](context: ActionArguments, data: Textbaustein): Promise<void>;
  [ActionTypes.DeleteTextbaustein](context: ActionArguments, data: DeleteTextbausteinData): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  //#region Vorlagen
  async [ActionTypes.GetVorlagen]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.getVorlageLoading = true;
    status.vorlageErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    commit(MutationTypes.SetVorlagen, new Array<Vorlage>());

    return ApiService.getAllVorlagen()
      .then((res) => {
        const data: Array<Vorlage> = res.data;
        commit(MutationTypes.SetVorlagen, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vorlageErrorMsg = error.response.data.detail;
        } else {
          status.vorlageErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getVorlageLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetVorlagenLight]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.getVorlageLoading = true;
    status.vorlageErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    commit(MutationTypes.SetVorlageItems, new Array<VorlageItem>());

    return ApiService.getAllVorlagenLight()
      .then((res) => {
        const data: Array<VorlageItem> = res.data;
        commit(MutationTypes.SetVorlageItems, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vorlageErrorMsg = error.response.data.detail;
        } else {
          status.vorlageErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getVorlageLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetVorlageById]({ commit, dispatch, rootGetters }: ActionArguments, data: number) {
    const status = rootGetters.status;
    status.getVorlageLoading = true;
    status.vorlageErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getVorlageById(data)
      .then((res) => {
        const data: Vorlage = res.data;
        commit(MutationTypes.AddOrUpdateVorlage, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vorlageErrorMsg = error.response.data.detail;
        } else {
          status.vorlageErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
      })
      .finally(() => {
        status.getVorlageLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.GetVorlagenPage]({ commit, dispatch, rootGetters }: ActionArguments, data: PagedRequest) {
    const status = rootGetters.status;
    status.getVorlageLoading = true;
    status.vorlageErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.getVorlagenPage(data)
      .then((res) => {
        const data: PagedResult<VorlageItem> = res.data;
        commit(MutationTypes.SetVorlagePageData, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.getVorlageLoading = error.response.data.detail;
        } else {
          status.getVorlageLoading = 'Es ist ein Fehler aufgetreten';
        }
      })
      .finally(() => {
        status.getVorlageLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.CreateVorlage]({ commit, dispatch, rootGetters }: ActionArguments, data: Vorlage) {
    const status = rootGetters.status;
    status.createVorlageLoading = true;
    status.vorlageErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.createVorlage(data)
      .then((res) => {
        const data: Vorlage = res.data;
        commit(MutationTypes.AddVorlage, data);
      })
      .catch((error) => {
        if (error.response.status == 400) {
          if (error.response.data.errors) {
            for (const key in error.response.data.errors) {
              status.vorlageErrorMsg += error.response.data.errors[key] + '\n';
            }
          } else {
            status.vorlageErrorMsg = error.response.data.title;
          }
        } else if (error.response.status == 404) {
          status.vorlageErrorMsg = error.response.data.detail;
        } else {
          status.vorlageErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.createVorlageLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateVorlage]({ commit, dispatch, rootGetters }: ActionArguments, data: Vorlage) {
    const status = rootGetters.status;
    status.updateVorlageLoading = true;
    status.vorlageErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.updateVorlage(data)
      .then((res) => {
        const data: Vorlage = res.data;
        commit(MutationTypes.AddOrUpdateVorlage, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.vorlageErrorMsg = error.response.data.detail;
        } else {
          status.vorlageErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.updateVorlageLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.DeleteVorlage]({ commit, dispatch, rootGetters }: ActionArguments, vorlageid: number) {
    const status = rootGetters.status;
    status.deleteVorlageLoading = true;
    status.vorlageErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.deleteVorlage(vorlageid)
      .then((res) => {
        commit(MutationTypes.DeleteVorlage, vorlageid);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 400) {
          status.vorlageErrorMsg = error.response.data.title;
        } else if (error.response.status == 404) {
          status.vorlageErrorMsg = error.response.data.detail;
        } else {
          status.vorlageErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.deleteVorlageLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
  //#endregion

  //#region Textbausteine
  async [ActionTypes.GetTextbausteine]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.getTextbausteineLoading = true;
    status.textbausteinErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    commit(MutationTypes.SetTextbausteine, new Array<Textbaustein>());

    return ApiService.getAllTextbausteine()
      .then((res) => {
        const data: Array<Textbaustein> = res.data;
        commit(MutationTypes.SetTextbausteine, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.textbausteinErrorMsg = error.response.data.detail;
        } else {
          status.textbausteinErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getTextbausteineLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.CreateTextbaustein]({ commit, dispatch, rootGetters }: ActionArguments, data: Textbaustein) {
    const status = rootGetters.status;
    status.createTextbausteinLoading = true;
    status.textbausteinErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.createTextbaustein(data)
      .then((res) => {
        const data: Textbaustein = res.data;
        commit(MutationTypes.AddTextbaustein, data);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 404) {
          status.textbausteinErrorMsg = error.response.data.detail;
        } else {
          status.textbausteinErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.createTextbausteinLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateTextbaustein]({ commit, dispatch, rootGetters }: ActionArguments, data: Textbaustein) {
    const status = rootGetters.status;
    status.updateTextbausteinLoading = true;
    status.textbausteinErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.updateTextbaustein(data)
      .then((res) => {
        const data: Textbaustein = res.data;
        commit(MutationTypes.AddOrUpdateTextbaustein, data);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          status.textbausteinErrorMsg = error.response.data.detail;
        } else {
          status.textbausteinErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.updateTextbausteinLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.DeleteTextbaustein]({ commit, dispatch, rootGetters }: ActionArguments, data: DeleteTextbausteinData) {
    const status = rootGetters.status;
    status.deleteTextbausteinLoading = true;
    status.textbausteinErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.deleteTextbaustein(data.id, data.forceDelete)
      .then(() => {
        commit(MutationTypes.DeleteTextbaustein, data.id);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 400) {
          status.textbausteinErrorMsg = error.response.data.title;
        } else if (error.response.status == 404) {
          status.textbausteinErrorMsg = error.response.data.detail;
        } else {
          status.textbausteinErrorMsg = 'Es ist ein unbekannter Fehler aufgetreten.';
        }
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.deleteTextbausteinLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
  //#endregion
};
